import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  Divider
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';

const SENDER_ADDRESS = 'invoices@smartinventories.io';

// Fixed light palette on purpose: the right pane previews the actual email,
// which always renders on a light background regardless of app theme.
const preview = {
  backdrop: '#f4f5f7',
  card: '#ffffff',
  ink: '#1f2733',
  soft: '#3a4452',
  muted: '#8a93a1',
  mutedDark: '#5a6472',
  tint: '#eceff3',
  border: '#e4e7eb',
};

// Compose-and-send dialog for emailing an issued invoice: form on the left,
// a live preview of the exact email on the right. The PDF is always attached
// server-side; the body is prefilled but fully editable.
const InvoiceEmailDialog = ({ open, onClose, invoice, invoiceClient, onSent }) => {
  const { t, i18n } = useTranslation();

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const company = invoice?.companySnapshot || {};
  const companyName = company.name || 'SmartInventory';

  useEffect(() => {
    if (!open || !invoice) return;
    setTo(invoice.recipient?.email || '');
    setCc('');
    setSubject(t('Invoice {{number}} from {{company}}', {
      number: invoice.invoiceNumber,
      company: companyName,
      defaultValue: `Invoice ${invoice.invoiceNumber} from ${companyName}`,
    }));
    setMessage(t(
      'invoiceEmailDefaultBody',
      {
        recipient: invoice.recipient?.name || '',
        company: companyName,
        defaultValue: `To: ${invoice.recipient?.name || ''}\n\nWe appreciate your business. Please find your invoice attached. Feel free to contact us if you have any questions.\n\nHave a great day!\n\n${companyName}`,
      }
    ));
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice]);

  const handleClose = () => {
    if (sending) return;
    setError('');
    onClose();
  };

  const blocked = !to.trim() || !subject.trim();

  const handleSend = async () => {
    if (sending || blocked || !invoice) return;
    setError('');
    setSending(true);
    try {
      const response = await invoiceClient.sendInvoiceEmail(invoice._id, {
        to: to.trim(),
        cc: cc.trim(),
        subject: subject.trim(),
        message,
      });
      onSent(response.data);
    } catch (err) {
      console.error('Error sending invoice email', err);
      setError(err.response?.data?.error || t('Something went wrong'));
    } finally {
      setSending(false);
    }
  };

  const fieldRow = (label, control) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 64, pt: 1, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{control}</Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {t('Send invoice {{number}}', {
          number: invoice?.invoiceNumber || '',
          defaultValue: `Send invoice ${invoice?.invoiceNumber || ''}`,
        })}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {fieldRow(t('From', { defaultValue: 'From' }), (
              <TextField
                fullWidth
                size="small"
                value={`${companyName} <${SENDER_ADDRESS}>`}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
              />
            ))}
            {fieldRow(t('To', { defaultValue: 'To' }), (
              <TextField
                fullWidth
                size="small"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder={t('name@company.com', { defaultValue: 'name@company.com' })}
                error={!to.trim()}
              />
            ))}
            {fieldRow(t('Cc', { defaultValue: 'Cc' }), (
              <TextField
                fullWidth
                size="small"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder={t('Separate addresses with commas', { defaultValue: 'Separate addresses with commas' })}
              />
            ))}
            {fieldRow(t('Subject', { defaultValue: 'Subject' }), (
              <TextField
                fullWidth
                size="small"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                error={!subject.trim()}
              />
            ))}

            <Box sx={{ pl: '78px', mb: 1.5 }}>
              <Chip
                size="small"
                variant="outlined"
                icon={<AttachFileIcon />}
                label={t('Invoice PDF attached', { defaultValue: 'Invoice PDF attached' })}
              />
            </Box>

            {fieldRow(t('Email body', { defaultValue: 'Email body' }), (
              <TextField
                fullWidth
                multiline
                minRows={9}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            ))}
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                backgroundColor: preview.backdrop,
                borderRadius: `${radius.lg}px`,
                p: { xs: 2, md: 3 },
                height: '100%',
                minHeight: 420,
              }}
            >
              <Box
                sx={{
                  backgroundColor: preview.card,
                  borderRadius: `${radius.md}px`,
                  px: 4,
                  py: 3.5,
                  maxWidth: 480,
                  mx: 'auto',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px rgba(31,39,51,0.08)',
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, color: preview.muted, mb: 2.5 }}>
                  {t('INVOICE {{number}} DETAILS', {
                    number: invoice?.invoiceNumber,
                    defaultValue: `INVOICE ${invoice?.invoiceNumber} DETAILS`,
                  })}
                </Typography>

                {company.logo && (
                  <Box component="img" src={company.logo} alt={companyName} sx={{ maxHeight: 48, mb: 1.5 }} />
                )}
                <Typography sx={{ fontSize: 18, fontWeight: 700, letterSpacing: 1, color: preview.ink, mb: 3 }}>
                  {companyName}
                </Typography>

                <Box sx={{ backgroundColor: preview.tint, borderRadius: `${radius.md}px`, py: 3, px: 2, mb: 3 }}>
                  {invoice?.dueDate && (
                    <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1, color: preview.mutedDark, mb: 0.75 }}>
                      {t('DUE {{date}}', {
                        date: new Date(invoice.dueDate).toLocaleDateString().toUpperCase(),
                        defaultValue: `DUE ${new Date(invoice.dueDate).toLocaleDateString().toUpperCase()}`,
                      })}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 34, fontWeight: 800, color: preview.ink }}>
                    {formatCurrency(invoice?.total, invoice?.currency, i18n.language)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: preview.mutedDark, mt: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <AttachFileIcon sx={{ fontSize: 14 }} />
                    {t('Invoice PDF attached', { defaultValue: 'Invoice PDF attached' })}
                  </Typography>
                </Box>

                <Typography sx={{ fontSize: 14, lineHeight: 1.65, color: preview.soft, textAlign: 'left', whiteSpace: 'pre-line', mb: 3 }}>
                  {message}
                </Typography>

                <Divider sx={{ borderColor: preview.border, mb: 2 }} />
                <Typography sx={{ fontSize: 11, color: preview.muted }}>
                  {t('Powered by SmartInventories.io', { defaultValue: 'Powered by SmartInventories.io' })}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('Cancel')}</Button>
        <Button
          onClick={handleSend}
          variant="contained"
          startIcon={sending ? undefined : <SendIcon />}
          disabled={blocked || sending}
        >
          {sending ? <CircularProgress size={20} /> : t('Send email', { defaultValue: 'Send email' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceEmailDialog;
